import { useMemo } from 'react';
import { useDynamicEntity } from './useDynamicEntity';
import { useAuth, useTags } from './index';
import { FORM_CONFIGS } from '../constants/formConfigurations';
import { hasuraClient } from '../utils/graphqlClient';
import { UPDATE_JOB_TAGS } from '../graphql/mutations/jobs';
import { useAccessibleFirmIds } from './useAccessibleFirmIds';

let globalSelectedContractWorkStatus = null;

// Work status name'den job_status_enum'a mapping
const mapWorkStatusToJobStatus = (workStatusName) => {
  const statusMapping = {
    'Yazımda': 'YAZIMDA',
    'Değerlendirmede': 'DEGERLENDIRMEDE',
    'Onaylandı': 'ONAYLANDI',
    'Reddedildi': 'REDDEDILDI',
    'Kapatıldı': 'KAPATILDI',
    'Danışmanlık Sonlandırıldı': 'DANISMANLIK_SONLANDIRILDI',
    'Tamamlandı': 'TAMAMLANDI',
    'Ortak Bulundu': 'ORTAK_BULUNDU',
    '1. Aşama Yazımda': 'ASAMA_BIR_YAZIMDA',
    '1. Aşama Değerlendirmede': 'ASAMA_BIR_DEGERLENDIRMEDE',
    '1. Aşama Onaylandı': 'ASAMA_BIR_ONAYLANDI',
    '2. Aşama Yazımda': 'ASAMA_IKI_YAZIMDA',
    '2. Aşama Değerlendirmede': 'ASAMA_IKI_DEGERLENDIRMEDE',
    '2. Aşama Onaylandı': 'ASAMA_IKI_ONAYLANDI',
    'Devralındı': 'DEVRALINDI',
    // Enum değerleri zaten doğruysa olduğu gibi döndür
    'YAZIMDA': 'YAZIMDA',
    'DEGERLENDIRMEDE': 'DEGERLENDIRMEDE',
    'ONAYLANDI': 'ONAYLANDI',
    'REDDEDILDI': 'REDDEDILDI',
    'KAPATILDI': 'KAPATILDI',
    'DANISMANLIK_SONLANDIRILDI': 'DANISMANLIK_SONLANDIRILDI',
    'TAMAMLANDI': 'TAMAMLANDI',
    'ORTAK_BULUNDU': 'ORTAK_BULUNDU',
    'ASAMA_BIR_YAZIMDA': 'ASAMA_BIR_YAZIMDA',
    'ASAMA_BIR_DEGERLENDIRMEDE': 'ASAMA_BIR_DEGERLENDIRMEDE',
    'ASAMA_BIR_ONAYLANDI': 'ASAMA_BIR_ONAYLANDI',
    'ASAMA_IKI_YAZIMDA': 'ASAMA_IKI_YAZIMDA',
    'ASAMA_IKI_DEGERLENDIRMEDE': 'ASAMA_IKI_DEGERLENDIRMEDE',
    'ASAMA_IKI_ONAYLANDI': 'ASAMA_IKI_ONAYLANDI',
    'DEVRALINDI': 'DEVRALINDI',
  };
  
  return statusMapping[workStatusName] || workStatusName; // Eğer mapping yoksa olduğu gibi döndür
};

export const setGlobalSelectedContractWorkStatus = (status) => {
  globalSelectedContractWorkStatus = mapWorkStatusToJobStatus(status);
};

const useJobs = ({ paginated = false } = {}) => {
  const { user } = useAuth();
  const { tags } = useTags();
  const { firmIds, loading: firmIdsLoading, isRestricted } = useAccessibleFirmIds();

  const customWhereCondition = useMemo(() => {
    if (user?.role === 'ROOT') return {};
    if (isRestricted) {
      const safeIds = firmIds && firmIds.length > 0 ? firmIds : [-1];
      return { company_id: { _eq: user?.companyId }, firm_id: { _in: safeIds } };
    }
    return { company_id: { _eq: user?.companyId } };
  }, [user?.role, user?.companyId, isRestricted, firmIds]);

  const firmIdsKey = isRestricted ? (firmIds ? firmIds.join(',') : 'loading') : 'unrestricted';
  
  const dynamicHook = useDynamicEntity({
    entityType: 'jobs',
    gqlEntityKey: 'jobs',
    queryName: 'GET_JOBS',
    createMutation: 'CREATE_JOB',
    updateMutation: 'UPDATE_JOB',
    deleteMutation: 'DELETE_JOB',
    onSuccess: async (action, result, formData) => {
      globalSelectedContractWorkStatus = null;

      if (formData.tagIds && formData.tagIds.length > 0) {
        try {
          const objects = formData.tagIds.map(tagId => ({
            job_id: result.id,
            tag_id: tagId,
          }));
          await hasuraClient.mutate(UPDATE_JOB_TAGS, {
            job_id: result.id,
            objects: objects,
          });
        } catch (error) {
          console.error('Error updating job tags:', error);
        }
      }

      // Auto-add to matching active flows. Best-effort, never blocks.
      // Dynamic import keeps this hook free of report-management graph deps
      // and prevents any chance of a circular import via index re-exports.
      try {
        if (action === 'create' || action === 'update') {
          const jobId = result?.id || result?.job?.id;
          if (jobId) {
            const { autoAddJobToFlowsStandalone } = await import('../utils/flowAutoAdd');
            await autoAddJobToFlowsStandalone(jobId);
          }
        }
      } catch (err) {
        console.error('Auto-add to flows failed:', err);
        // Non-blocking: do not rethrow
      }
    },
    transformData: (job) => ({
      ...job,
      jobTitle: job.job_title,
      // Eski sayfalar job.job_type (enum string) okumaya devam ediyor; yeni
      // sayfalar job.job_type relationship objesini bekliyor. Transition
      // suresince ikisini de surfaye ediyoruz: enum string `job_type_enum`
      // anahtarinda, iliski objesi ise `job_type` anahtarinda yer alir.
      job_type_enum: job.job_type,
      job_type: job.job_type_obj || (job.job_type
        ? { id: null, name: job.job_type, is_project_based: null, status_group: null }
        : null),
      job_type_id: job.job_type_id,
      jobType: job.job_type_obj?.name || job.job_type,
      jobTypeId: job.job_type_id,
      jobStatus: job.contract?.work_status?.name,
      contractId: job.contract_id,
      companyName: job.company?.name,
      company_name: job.company?.name, // For filter compatibility
      companyId: job.company_id,
      description: job.description_of_job,
      createdByUser: job.user ? `${job.user.first_name} ${job.user.last_name}` : '-',
      firm_name: job.contract?.firm?.firm_name || '-',
      firm_short_name: job.contract?.firm?.firm_short_name || '-',
      tagIds: job.job_tags?.map(jt => jt.tag_id) || [],
      tags: job.job_tags?.map(jt => jt.tag).filter(Boolean) || [],
      post_approval_report_required: Boolean(job.post_approval_report_required),
      post_completion_report_required: Boolean(job.post_completion_report_required),
      job_reports: job.job_reports || [],
      projectNumber: job.project_number || '',
      teknoparkUnit: job.teknopark_unit || '',
      consultingScope: job.consulting_scope || 'MALI_TEKNIK',
      contractInfo: job.contract ? {
        subject: job.contract.contract_subject?.name,
        scopeOfWork: job.contract.scope_of_work?.name,
        signingDate: job.contract.contract_signing_date,
        workStartDate: job.contract.work_start_date,
        workStatus: job.contract.work_status?.name,
        firmName: job.contract.firm?.firm_name,
        firmShortName: job.contract.firm?.firm_short_name,
        pricingMethod: job.contract.pricing_method?.name
      } : null,
      job_assignment: job.job_assignment,
      technical_responsible_name: job.job_assignment?.technical_user
        ? `${job.job_assignment.technical_user.first_name || ''} ${job.job_assignment.technical_user.last_name || ''}`.trim() || '-'
        : '-',
      financial_responsible_name: job.job_assignment?.financial_user
        ? `${job.job_assignment.financial_user.first_name || ''} ${job.job_assignment.financial_user.last_name || ''}`.trim() || '-'
        : '-',
      // Filter dropdown user_id üzerinden çalışıyor — display name'i ayrı
      // alanda tutuyoruz; client-filter ve server-filter ikisi de bu numeric
      // alandan eşleşir (hasuraField: job_assignment.technical_user_id).
      technical_user_id: job.job_assignment?.technical_user_id ?? null,
      financial_user_id: job.job_assignment?.financial_user_id ?? null,
    }),
            transformFormData: (formData) => {
      let firm_id = null;
      let firm_short_name = null;
      
      if (formData.firm_established === 'yes') {
        firm_id = formData.firmId;
        firm_short_name = null;
      } else {
        // firm_short_name seçimi aslında firm_id'yi temsil ediyor
        firm_id = formData.firm_short_name; // Bu artık firm_id
        firm_short_name = null;
      }
      
      let job_status = formData.jobStatus;
      if (formData.contractId && globalSelectedContractWorkStatus) {
        job_status = globalSelectedContractWorkStatus;
      }
      
      if (job_status && typeof job_status === 'string') {
        const originalStatus = job_status;
        job_status = mapWorkStatusToJobStatus(job_status);
      }
      // Sadece job_type_id (yeni FK) gonderiliyor. Legacy public.job_type
      // tablosu sabit 4 isimle sinirli ve yeni dinamik turler oraya
      // eklenmedigi icin FK ihlali yapardi (migration 1783100000000 kolonu
      // nullable yapip FK'yi kaldirdi; yeni gelen ekleme/duzenlemeler artik
      // sadece dinamik FK uzerinden ilerler).
      const jobTypeIdInt = formData.jobTypeId
        ? parseInt(formData.jobTypeId, 10)
        : null;
      const payload = {
        job_type_id: Number.isNaN(jobTypeIdInt) ? null : jobTypeIdInt,
        job_title: formData.jobTitle,
        description_of_job: formData.description,
        project_number: formData.projectNumber || null,
        teknopark_unit: (formData.teknoparkUnit || '').trim().slice(0, 15) || null,
        consulting_scope: formData.consultingScope || 'MALI_TEKNIK',
        contract_id: formData.contractId,
        company_id: formData.companyId || user?.companyId,
        created_by: user?.id,
        firm_id: firm_id,
        firm_short_name: firm_short_name,
        firm_established: formData.firm_established === 'yes',
      };
      return payload;
    },
    customWhereCondition,
    customDepsKey: firmIdsKey,

    mutationPattern: 'jobs',
    createVariableName: 'job',
    updateVariableName: '_set',
    serverPagination: paginated,
    aggregateKey: paginated ? 'jobs_aggregate' : null,
  });


  if (tags && tags.length > 0) {
    const tagIdsField = FORM_CONFIGS.jobs.fields.find(field => field.name === 'tagIds');
    if (tagIdsField) {
      tagIdsField.options = tags.map(tag => ({
        value: tag.id,
        label: tag.name,
      }));
    }
  }

  return {
    ...dynamicHook,
    isLoading: dynamicHook.isLoading || (isRestricted && firmIdsLoading),
    isRootUser: user?.role === 'ROOT',
  };
};

export { useJobs };
